import { FormField, Select, SelectProps } from '@cloudscape-design/components';
import { Role } from 'src/types/orbit/Roles';
import { convertItem, convertList } from 'src/utils/generalUtils';
import { useMemo, useCallback, memo } from 'react';
import { isEqual, pick } from 'lodash';

interface RoleListDropdownProps {
  roleList: Role[];
  isLoading: boolean;
  error?: string | null;
  selectedRole?: Role | null;
  onRoleChange: (role: Role) => void;

  readOnly?: boolean;
}

const RoleListDropdown = ({
  selectedRole,
  roleList,
  isLoading,
  error,
  onRoleChange,
  readOnly,
}: RoleListDropdownProps) => {
  // Memoize the selected role conversion
  const selectedRoleOption = useMemo(() => {
    if (!selectedRole) return null;
    return convertItem<Role, SelectProps.Option>(selectedRole, (item) => ({
      value: item.roleId,
      label: item.name,
    }));
  }, [selectedRole]);

  // Memoize the roles list conversion
  const roleOptions = useMemo(() => {
    return convertList<Role, SelectProps.Option>(roleList, (item) => ({
      value: item.roleId,
      label: item.name,
    }));
  }, [roleList]);

  // Memoize the onChange handler
  const handleChange = useCallback(
    ({ detail }: { detail: SelectProps.ChangeDetail }) => {
      if (detail.selectedOption?.value) {
        const selectedRole = roleList.find(
          (role) => role.roleId === detail.selectedOption.value,
        );
        if (selectedRole) {
          onRoleChange(selectedRole);
        }
      }
    },
    [roleList, onRoleChange],
  );

  // Memoize the status type calculation
  const statusType = useMemo(() => {
    if (isLoading) return 'loading';
    if (error) return 'error';
    return 'finished';
  }, [isLoading, error]);

  return (
    <Select
      readOnly={readOnly}
      data-testid="role-dropdown"
      options={roleOptions}
      selectedOption={selectedRoleOption}
      onChange={handleChange}
      errorText={error || undefined}
      loadingText="Loading roles"
      statusType={statusType}
      placeholder="Choose a role"
    />
  );
};

// Memoize the entire component if needed
export default memo(RoleListDropdown, (prevProps, nextProps) => {
  // Using Lodash's isEqual for deep comparison
  return (
    isEqual(
      pick(prevProps, [
        'roleList',
        'isLoading',
        'error',
        'title',
        'formFieldError',
        'readOnly',
      ]),
      pick(nextProps, [
        'roleList',
        'isLoading',
        'error',
        'title',
        'formFieldError',
        'readOnly',
      ]),
    ) && isEqual(prevProps.selectedRole?.roleId, nextProps.selectedRole?.roleId)
  );
});
